import { useEffect } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '../store'
import { fetchCurrentUser } from '../slices/authSlice'
import { login } from '../components/UserMenu'
import { useGetInviteInfoQuery } from '../services/projectDataApi'
import Spinner from '../components/Spinner'
import './InvitePage.css'

// The branded landing an invited person reaches from the project-invite email
// (netlify/functions/lib/project-invite-email.ts → /invite/:projectId). Invitees are usually
// logged-out — and may not have a Replay QA account at all — so instead of dropping them onto the
// auth-gated project overview we greet them here with the project's name + thumbnail and a note about
// what joining lets them do, then offer two paths: sign in, or create an account. Both routes send
// them on to the project overview, where signing in with the invited address claims their pending
// membership (see claimPendingMemberships / auth-me).
export default function InvitePage() {
  const { projectId } = useParams<{ projectId: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const { user, checked, loading } = useSelector((state: RootState) => state.auth)

  // Resolve the current session so an already-signed-in visitor skips the landing and goes straight
  // to the project (their invite is claimed on load), and a logged-out one sees the CTAs.
  useEffect(() => {
    if (!checked) dispatch(fetchCurrentUser())
  }, [dispatch, checked])

  const { data: info, isLoading: infoLoading } = useGetInviteInfoQuery(projectId ?? '', {
    skip: !projectId,
  })

  if (!projectId) return <Navigate to="/" replace />

  const overviewPath = `/projects/${projectId}/overview`

  // Already signed in → the landing has nothing to add; go to the project.
  if (checked && !loading && user) return <Navigate to={overviewPath} replace />

  // Wait for the auth check before deciding what to show (avoids flashing the CTAs at a signed-in
  // user we're about to redirect). The invite info is waited on too: it names the project in the
  // heading and supplies the thumbnail, so rendering ahead of it greets the invitee with a generic
  // "a Replay QA project" and a blank frame that both then rewrite themselves.
  if (!checked || loading || infoLoading) {
    return (
      <div className="invite-page">
        <div className="invite-page__loading">
          <Spinner size={28} />
        </div>
      </div>
    )
  }

  const projectName = info?.name?.trim() || null
  const heading = projectName ? `You’ve been invited to ${projectName}` : 'You’ve been invited to a Replay QA project'

  return (
    <div className="invite-page">
      <div className="invite-page__card" data-testid="invite-page">
        <div className="invite-page__brand">
          <img src="/images/logo.svg" alt="" width="26" height="30" className="invite-page__logo" />
          <span className="invite-page__wordmark">Replay QA</span>
        </div>

        <div className="invite-page__thumb-frame">
          {info?.thumbnailUrl ? (
            <img
              src={info.thumbnailUrl}
              alt={projectName ? `${projectName} preview` : 'Project preview'}
              className="invite-page__thumb"
            />
          ) : (
            <img
              src="/images/project-placeholder.svg"
              alt=""
              className="invite-page__thumb invite-page__thumb--placeholder"
            />
          )}
        </div>

        <h1 className="invite-page__title">{heading}</h1>
        <p className="invite-page__note">
          Replay QA automatically explores this app, files the bugs it finds, and keeps testing as the
          app changes. Once you join you can view its QA results, the bugs it’s found, and the user
          journeys it tests.
        </p>

        <div className="invite-page__actions">
          <div className="invite-page__action">
            <span className="invite-page__action-label">Already have a Replay QA account?</span>
            <button
              type="button"
              className="invite-page__btn invite-page__btn--primary"
              data-testid="invite-sign-in"
              data-analytics-event="invite-sign-in"
              onClick={() => login(dispatch, overviewPath)}
            >
              Sign In
            </button>
          </div>

          <div className="invite-page__action">
            <span className="invite-page__action-label">New to Replay QA?</span>
            <button
              type="button"
              className="invite-page__btn invite-page__btn--secondary"
              data-testid="invite-create-account"
              data-analytics-event="invite-create-account"
              onClick={() => login(dispatch, overviewPath, 'signup')}
            >
              Create an account
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
